import type { ReactNode } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAuthModal } from '../../hooks/authModalContext'
import { useAssertVertical, verticalThemeVars } from '../layout/useVertical'
import { SwitchServiceButton } from '../layout/SwitchServiceButton'
import { VerticalBrand } from '../layout/VerticalBrand'
import { AppShell } from '../layout/AppShell'
import { NotificationBell } from '../layout/NotificationBell'
import { RoleSwitcherButton } from '../layout/RoleSwitcher'
import { MovesSearchBar } from './MovesSearchBar'
import {
  MovesSearchContext,
  useMovesSearch,
  useMovesSearchState,
} from './MovesSearchContext'

/** The search pill, bound to the shared moves search context. */
function HeaderSearch() {
  const { search, setSearch } = useMovesSearch()
  return <MovesSearchBar search={search} onChange={setSearch} />
}

/**
 * Moves-specific public header — no discovery tabs; the search pill sits in the
 * header row next to the logo and sign-in (same pattern as thrifts).
 */
function MovesPublicHeader() {
  const { isAuthenticated, logout } = useAuth()
  const { openAuthModal } = useAuthModal()

  return (
    <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/95 backdrop-blur">
      <div className="grid w-full grid-cols-[auto_auto] items-center gap-3 px-4 py-3 md:grid-cols-[auto_minmax(0,1fr)_auto] md:px-6">
        <Link
          to="/moves"
          title="Moves home"
          aria-label="Moves home"
          className="flex items-center gap-3 text-stone-950"
        >
          <VerticalBrand vertical="moves" />
        </Link>
        <div className="col-span-2 row-start-2 min-w-0 md:col-span-1 md:row-start-auto">
          <HeaderSearch />
        </div>
        {isAuthenticated ? (
          <div className="flex items-center justify-end gap-3">
            <div className="hidden sm:block">
              <RoleSwitcherButton />
            </div>
            <SwitchServiceButton />
            <NotificationBell />
            <button
              onClick={logout}
              className="hidden items-center gap-2 rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100 sm:inline-flex"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <SwitchServiceButton />
            <button
              type="button"
              onClick={openAuthModal}
              className="rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

/**
 * Route layout owning the search shared by Moves discovery surfaces.
 * Above the routes so the query survives navigating within the vertical.
 */
export function MovesSearchLayout() {
  const searchValue = useMovesSearchState()
  return (
    <MovesSearchContext.Provider value={searchValue}>
      <Outlet />
    </MovesSearchContext.Provider>
  )
}

/**
 * Page frame for Moves discovery. Replaces the shared discovery header (and its
 * single "Moves" tab) with a header that carries the From / To / Load search pill.
 * Authenticated users keep AppShell; the pill sits in the header center.
 */
export function MovesDiscoveryFrame({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  useAssertVertical('moves')

  return isAuthenticated ? (
    <AppShell showWorkspaceBanner={false} headerCenter={<HeaderSearch />}>
      <div className="space-y-4">{children}</div>
    </AppShell>
  ) : (
    <div className="min-h-screen bg-stone-100" style={verticalThemeVars('moves')}>
      <MovesPublicHeader />
      <main className="mx-auto max-w-[1500px] px-4 py-4 md:px-6 md:py-6">
        <div className="space-y-4">{children}</div>
      </main>
    </div>
  )
}
